<template>
  <div class="dashboard-budget">
    <nav class="page-breadcrumb mb-1">
      <feather-icon icon="HomeIcon" size="14" />
      <span class="ml-25">Budget</span>
      <feather-icon icon="ChevronRightIcon" size="14" class="mx-25" />
      <span class="font-weight-bold">{{ ehRepresentante ? 'Minha Verba' : 'Dashboard de Budget' }}</span>
    </nav>

    <div v-if="isLoading || carregando" class="text-center py-5"><b-spinner /></div>

    <!-- ─────────────── Visão do REPRESENTANTE (consulta pessoal) ─────────────── -->
    <template v-else-if="ehRepresentante">
      <!-- Sem budget vigente em nenhum vínculo: nada de cards/saldo, só aviso. -->
      <template v-if="meuSaldo && meuSaldo.semBudget">
        <b-alert show variant="warning" class="mb-1">
          <div class="p-50">
            <feather-icon icon="AlertTriangleIcon" class="mr-50" />
            Não há budget vigente para a sua carteira no momento. Quando o seu
            supervisor publicar um budget vigente, sua verba aparecerá aqui.
            <!-- Detalhe por categoria: budget vencido/futuro (senão o rep não
                 entende por que o saldo "sumiu"). -->
            <div v-for="cat in ['ELT', 'GL']" :key="`sem-budget-${cat}`">
              <small v-if="avisoBudgetNaoVigente(cat)" class="d-block mt-25">
                <b-badge :variant="cat === 'ELT' ? 'light-primary' : 'light-info'" class="mr-25">{{ cat }}</b-badge>
                {{ avisoBudgetNaoVigente(cat) }}
              </small>
            </div>
          </div>
        </b-alert>
      </template>

      <template v-else>
      <b-alert v-if="meuSaldo && meuSaldo.saldoParcial" show variant="warning" class="mb-1">
        <div class="p-50">Consulta parcial: os totais incluem apenas categorias resolvidas.
          Responsabilidade pendente não significa saldo zero nem doação sem controle de budget.</div>
      </b-alert>
      <b-alert v-if="meuSaldo && meuSaldo.semAlocacao" show variant="warning" class="mb-1">
        <div class="p-50">
          <feather-icon icon="AlertTriangleIcon" class="mr-50" />
          Você ainda não possui verba (alocação) em um budget vigente.
        </div>
      </b-alert>

      <!-- Uma linha de cards por categoria (ELT / GL) -->
      <div v-for="cat in ['ELT', 'GL']" :key="`rep-cat-${cat}`">
        <div class="d-flex align-items-center mb-50 mt-50">
          <b-badge :variant="cat === 'ELT' ? 'light-primary' : 'light-info'" class="mr-50">{{ cat }}</b-badge>
          <small class="text-muted">{{ cat === 'ELT' ? 'English Language Teaching' : 'Global Languages' }}</small>
          <!-- Aviso explícito quando o budget da categoria venceu/não começou:
               antes só aparecia um "sem budget vigente" discreto e o card de
               verba zerado confundia ("o saldo não some"). -->
          <small v-if="minhaCategoria(cat).indisponivel" class="text-warning font-weight-bold ml-auto">responsabilidade pendente</small>
          <small v-else-if="avisoBudgetNaoVigente(cat)" class="text-warning font-weight-bold ml-auto">
            <feather-icon icon="AlertTriangleIcon" size="12" class="mr-25" />{{ avisoBudgetNaoVigente(cat) }}
          </small>
          <small v-else-if="!minhaCategoria(cat).temBudget" class="text-muted ml-auto">sem budget vigente</small>
          <small v-else-if="!minhaCategoria(cat).temAlocacao" class="text-muted ml-auto">sem alocação</small>
        </div>
        <b-alert v-if="minhaCategoria(cat).indisponivel" show variant="warning">
          <div class="p-50">Saldo indisponível nesta categoria. Solicite ao administrador a revisão dos vínculos e alocações.
            Doações continuam sujeitas à validação de responsabilidade e saldo.</div>
        </b-alert>
        <b-row v-else>
          <b-col md="3" sm="6">
            <b-card class="text-center mb-1">
              <small class="text-muted d-block">Verba</small>
              <h4 class="mb-0">{{ formatarMoeda(minhaCategoria(cat).valor) }}</h4>
            </b-card>
          </b-col>
          <b-col md="3" sm="6">
            <b-card class="text-center mb-1">
              <small class="text-muted d-block">Gasto</small>
              <h4 class="mb-0 text-warning">{{ formatarMoeda(minhaCategoria(cat).valorgasto) }}</h4>
            </b-card>
          </b-col>
          <b-col md="3" sm="6">
            <b-card class="text-center mb-1">
              <small class="text-muted d-block">Em rascunho</small>
              <h4 class="mb-0" :class="Number(minhaCategoria(cat).rascunho) > 0 ? 'text-info' : 'text-muted'">
                {{ formatarMoeda(minhaCategoria(cat).rascunho) }}
              </h4>
              <small class="text-muted">pré-pedidos ainda não finalizados</small>
            </b-card>
          </b-col>
          <b-col md="3" sm="6">
            <b-card class="text-center mb-1">
              <small class="text-muted d-block">Disponível</small>
              <h4 class="mb-0" :class="Number(minhaCategoria(cat).disponivel) <= 0 ? 'text-danger' : 'text-success'">
                {{ formatarMoeda(minhaCategoria(cat).disponivel) }}
              </h4>
              <small v-if="Number(minhaCategoria(cat).rascunho) > 0" class="text-muted">
                após rascunhos:
                <span :class="Number(minhaCategoria(cat).disponivelAposRascunhos) <= 0 ? 'text-danger' : 'text-success'">
                  {{ formatarMoeda(minhaCategoria(cat).disponivelAposRascunhos) }}
                </span>
              </small>
            </b-card>
          </b-col>
        </b-row>
      </div>

      <b-card
        v-if="meuSaldo && meuSaldo.itens && meuSaldo.itens.length > 1"
        title="Por representante"
      >
        <p class="text-muted small mb-1">
          Você está vinculado a mais de um representante. Veja a verba de cada um.
        </p>
        <div
          v-for="item in meuSaldo.itens"
          :key="`${item.representanteid}-${item.categoria || ''}`"
          class="mb-1"
        >
          <div class="d-flex justify-content-between align-items-center mb-25">
            <span class="font-weight-bold">{{ item.representante_nome || item.representanteid }} <small v-if="item.categoria">({{ item.categoria }})</small></span>
            <span v-if="item.temAlocacao" class="small">
              <span class="text-muted">Disponível:</span>
              <span :class="Number(item.disponivel) <= 0 ? 'text-danger' : 'text-success'">
                {{ formatarMoeda(item.disponivel) }}
              </span>
              <span class="text-muted ml-50">de {{ formatarMoeda(item.valor) }}</span>
            </span>
            <span v-else-if="item.indisponivel" class="small text-warning">Responsabilidade pendente — saldo indisponível</span>
            <span v-else class="small text-muted">Sem alocação</span>
          </div>
          <b-progress
            v-if="item.temAlocacao"
            :max="Number(item.valor) || 1"
            height="0.6rem"
          >
            <b-progress-bar
              :value="Number(item.valorgasto) || 0"
              :variant="Number(item.disponivel) <= 0 ? 'danger' : 'success'"
            />
          </b-progress>
        </div>
      </b-card>

      <b-card title="Pedidos em rascunho">
        <p class="text-muted small mb-1">
          Pré-pedidos de doação (WD) ainda não finalizados. Eles ainda não
          consumiram sua verba, mas vão consumir quando forem finalizados.
        </p>
        <div v-if="!rascunhos.length" class="text-muted py-2">
          Nenhum pedido em rascunho no momento.
        </div>
        <b-table
          v-else
          :items="rascunhos"
          :fields="rascFields"
          small
          responsive
          hover
          class="mb-0"
        >
          <template #cell(pedidoid)="data">
            <span class="font-weight-bold">{{ data.item.pedidoid }}</span>
          </template>
          <template #cell(cliente_nome)="data">{{ data.item.cliente_nome || '-' }}</template>
          <template #cell(categoria)="data">
            <b-badge :variant="data.item.categoria === 'ELT' ? 'light-primary' : 'light-info'">
              {{ data.item.categoria }}
            </b-badge>
          </template>
          <template #cell(representante_nome)="data">{{ data.item.representante_nome || '-' }}</template>
          <template #cell(data)="data">{{ formatarDataHora(data.item.data) }}</template>
          <template #cell(valor)="data">
            <span class="text-info">{{ formatarMoeda(data.item.valor) }}</span>
          </template>
        </b-table>
        <div v-if="rascunhos.length" class="d-flex justify-content-end mt-1">
          <span class="text-muted mr-50">Total em rascunho:</span>
          <span class="font-weight-bold text-info">{{ formatarMoeda(totalRascunhos) }}</span>
        </div>
      </b-card>

      <b-card title="Pedidos que deram baixa">
        <p class="text-muted small mb-1">
          Doações (pedidos WD) que consumiram sua verba. Pedidos cancelados não são exibidos.
        </p>
        <div v-if="meuSaldo.saldoParcial" class="text-warning py-2">
          Histórico de baixas indisponível enquanto houver responsabilidade pendente.
          Isso não significa ausência de pedidos ou de consumo.
        </div>
        <div v-else-if="!pedidos.length" class="text-muted py-2">
          Nenhum pedido deu baixa na sua verba.
        </div>
        <b-table
          v-else
          :items="pedidos"
          :fields="pedFields"
          small
          responsive
          hover
          class="mb-0"
        >
          <template #cell(pedidoid)="data">
            <span class="font-weight-bold">{{ data.item.pedidoid || data.item.referencia }}</span>
          </template>
          <template #cell(cliente_nome)="data">{{ data.item.cliente_nome || '-' }}</template>
          <template #cell(oc_cliente)="data">{{ data.item.oc_cliente || '-' }}</template>
          <template #cell(pedido_valor_total)="data">
            {{ data.item.pedido_valor_total != null ? formatarMoeda(data.item.pedido_valor_total) : '-' }}
          </template>
          <template #cell(representante_nome)="data">{{ data.item.representante_nome || '-' }}</template>
          <template #cell(data)="data">{{ formatarDataHora(data.item.data) }}</template>
          <template #cell(valor)="data">
            <span class="text-danger">{{ formatarMoeda(data.item.valor) }}</span>
          </template>
          <template #cell(acoes)="data">
            <b-button
              size="sm"
              variant="outline-primary"
              :disabled="!data.item.clienteid"
              :title="data.item.clienteid ? 'Consultar pedido' : 'Pedido não localizado'"
              @click="verPedido(data.item)"
            >
              <feather-icon icon="EyeIcon" size="14" />
              <span class="ml-25">Consultar</span>
            </b-button>
          </template>
        </b-table>
        <div v-if="!meuSaldo.saldoParcial && pedidos.length" class="d-flex justify-content-end mt-1">
          <span class="text-muted mr-50">Total dado em baixa:</span>
          <span class="font-weight-bold text-danger">{{ formatarMoeda(totalBaixa) }}</span>
        </div>
      </b-card>
      </template>
    </template>

    <!-- ─────────────── Visão do SUPERVISOR / ADMIN (agregado) ─────────────── -->
    <template v-else-if="dashboard">
      <!-- Atuando como APROVADOR ALTERNATIVO (delegação recebida) -->
      <b-alert v-if="delegadoDe" show variant="info" class="mb-1">
        <div class="p-50">
          <feather-icon icon="UserCheckIcon" class="mr-50" />
          Você está atuando como <strong>aprovador alternativo</strong> de
          <strong>{{ delegadoDe.supervisor_nome || delegadoDe.supervisorid }}</strong>
          <span v-if="delegadoDe.datatermino"> até {{ formatarDataCurta(delegadoDe.datatermino) }}</span>.
          Todas as suas ações ficam registradas em seu nome.
        </div>
      </b-alert>

      <!-- Gestão da delegação (só o PRÓPRIO supervisor) -->
      <b-card v-if="ehSupervisorProprio" title="Aprovador alternativo (férias / ausência)">
        <p class="text-muted small mb-1">
          Designe um usuário para aprovar pedidos de doação e gerenciar o seu
          budget enquanto você estiver ausente. Apenas uma delegação ativa por
          vez; as ações do delegado ficam auditadas no nome dele.
        </p>
        <div v-if="delegacaoAtiva" class="d-flex align-items-center flex-wrap">
          <b-badge variant="light-success" class="mr-1 mb-50">Ativa</b-badge>
          <span class="mr-1 mb-50">
            <strong>{{ delegacaoAtiva.delegado_nome || delegacaoAtiva.delegadoid }}</strong>
            — de {{ formatarDataCurta(delegacaoAtiva.datainicio) }}
            {{ delegacaoAtiva.datatermino ? `até ${formatarDataCurta(delegacaoAtiva.datatermino)}` : '(sem prazo definido)' }}
            <span v-if="delegacaoAtiva.motivo" class="text-muted">· {{ delegacaoAtiva.motivo }}</span>
          </span>
          <b-button size="sm" variant="outline-danger" class="mb-50" :disabled="delegacaoSalvando" @click="encerrarDelegacao">
            Encerrar delegação
          </b-button>
        </div>
        <div v-else>
          <b-row>
            <b-col md="4" class="mb-50">
              <label class="small text-muted mb-25">Usuário delegado</label>
              <b-form-select v-model="delegacaoForm.delegadoid" :options="candidatosOptions" size="sm" />
            </b-col>
            <b-col md="2" class="mb-50">
              <label class="small text-muted mb-25">Início</label>
              <b-form-input v-model="delegacaoForm.datainicio" type="date" size="sm" />
            </b-col>
            <b-col md="2" class="mb-50">
              <label class="small text-muted mb-25">Término (opcional)</label>
              <b-form-input v-model="delegacaoForm.datatermino" type="date" size="sm" />
            </b-col>
            <b-col md="3" class="mb-50">
              <label class="small text-muted mb-25">Motivo (opcional)</label>
              <b-form-input v-model="delegacaoForm.motivo" placeholder="Ex.: férias" size="sm" />
            </b-col>
            <b-col md="1" class="mb-50 d-flex align-items-end">
              <b-button size="sm" variant="primary" :disabled="!delegacaoForm.delegadoid || delegacaoSalvando" @click="criarDelegacao">
                Designar
              </b-button>
            </b-col>
          </b-row>
          <small class="text-muted">Sem data de término, a delegação vale até você encerrá-la (ex.: desligamento).</small>
        </div>
      </b-card>

      <b-row>
        <b-col md="3" sm="6">
          <b-card class="text-center">
            <small class="text-muted d-block">Budgets</small>
            <h3 class="mb-0">{{ dashboard.resumo.budgets }}</h3>
            <small class="text-muted">{{ dashboard.resumo.vigentes }} vigentes</small>
          </b-card>
        </b-col>
        <b-col md="3" sm="6">
          <b-card class="text-center">
            <small class="text-muted d-block">Valor Total</small>
            <h3 class="mb-0">{{ formatarMoeda(dashboard.resumo.valor_total) }}</h3>
          </b-card>
        </b-col>
        <b-col md="3" sm="6">
          <b-card class="text-center">
            <small class="text-muted d-block">Gasto</small>
            <h3 class="mb-0 text-warning">{{ formatarMoeda(dashboard.resumo.gasto_total) }}</h3>
          </b-card>
        </b-col>
        <b-col md="3" sm="6">
          <b-card class="text-center">
            <small class="text-muted d-block">Saldo</small>
            <h3 class="mb-0" :class="dashboard.resumo.saldo_total <= 0 ? 'text-danger' : 'text-success'">
              {{ formatarMoeda(dashboard.resumo.saldo_total) }}
            </h3>
          </b-card>
        </b-col>
      </b-row>

      <!-- Resumo por categoria: uma linha de cards para ELT e outra para GL -->
      <div v-for="cat in ['ELT', 'GL']" :key="`cat-${cat}`">
        <div class="d-flex align-items-center mb-50 mt-50">
          <b-badge :variant="cat === 'ELT' ? 'light-primary' : 'light-info'" class="mr-50">{{ cat }}</b-badge>
          <small class="text-muted">{{ cat === 'ELT' ? 'English Language Teaching' : 'Global Languages' }}</small>
        </div>
        <b-row>
          <b-col md="3" sm="6">
            <b-card class="text-center mb-1">
              <small class="text-muted d-block">Budgets</small>
              <h4 class="mb-0">{{ categoriaResumo(cat).budgets }}</h4>
              <small class="text-muted">{{ categoriaResumo(cat).vigentes }} vigentes</small>
            </b-card>
          </b-col>
          <b-col md="3" sm="6">
            <b-card class="text-center mb-1">
              <small class="text-muted d-block">Valor Total</small>
              <h4 class="mb-0">{{ formatarMoeda(categoriaResumo(cat).valor_total) }}</h4>
            </b-card>
          </b-col>
          <b-col md="3" sm="6">
            <b-card class="text-center mb-1">
              <small class="text-muted d-block">Gasto</small>
              <h4 class="mb-0 text-warning">{{ formatarMoeda(categoriaResumo(cat).gasto_total) }}</h4>
            </b-card>
          </b-col>
          <b-col md="3" sm="6">
            <b-card class="text-center mb-1">
              <small class="text-muted d-block">Saldo</small>
              <h4 class="mb-0" :class="categoriaResumo(cat).saldo_total <= 0 ? 'text-danger' : 'text-success'">
                {{ formatarMoeda(categoriaResumo(cat).saldo_total) }}
              </h4>
              <small v-if="Number(categoriaResumo(cat).rascunho_total) > 0" class="text-muted">
                em rascunho:
                <span class="text-info">{{ formatarMoeda(categoriaResumo(cat).rascunho_total) }}</span>
              </small>
            </b-card>
          </b-col>
        </b-row>
      </div>

      <b-card :title="dashboard.escopo === 'supervisor' ? 'Meu budget' : 'Por supervisor'">
        <div v-if="!dashboard.por_supervisor || !dashboard.por_supervisor.length" class="text-muted">
          Nenhum budget encontrado.
        </div>
        <b-table
          v-else
          :items="dashboard.por_supervisor"
          :fields="supFields"
          small
          responsive
          class="mb-0"
        >
          <template #cell(supervisor_nome)="data">
            {{ data.item.supervisor_nome || data.item.supervisorid }}
          </template>
          <template #cell(categoria)="data">
            <b-badge v-if="data.item.categoria" :variant="data.item.categoria === 'ELT' ? 'light-primary' : 'light-info'">
              {{ data.item.categoria }}
            </b-badge>
            <span v-else class="text-muted">—</span>
          </template>
          <template #cell(valor)="data">{{ formatarMoeda(data.item.valor) }}</template>
          <template #cell(valorgasto)="data">{{ formatarMoeda(data.item.valorgasto) }}</template>
          <template #cell(saldo)="data">
            <span :class="Number(data.item.saldo) <= 0 ? 'text-danger' : 'text-success'">
              {{ formatarMoeda(data.item.saldo) }}
            </span>
          </template>
        </b-table>
      </b-card>

      <b-card title="Últimas movimentações">
        <div v-if="!dashboard.ultimas_movimentacoes || !dashboard.ultimas_movimentacoes.length" class="text-muted">
          Nenhuma movimentação registrada.
        </div>
        <b-table
          v-else
          :items="dashboard.ultimas_movimentacoes"
          :fields="movFields"
          small
          responsive
          hover
          class="mb-0"
          @row-clicked="irParaBudget"
        >
          <template #cell(datahora)="data">{{ formatarDataHora(data.item.datahora) }}</template>
          <template #cell(categoria)="data">
            <b-badge v-if="data.item.categoria" :variant="data.item.categoria === 'ELT' ? 'light-primary' : 'light-info'">
              {{ data.item.categoria }}
            </b-badge>
            <span v-else class="text-muted">—</span>
          </template>
          <template #cell(supervisor_nome)="data">{{ data.item.supervisor_nome || data.item.supervisorid }}</template>
          <template #cell(usuario_nome)="data">
            <span :title="data.item.usuario_id ? `ID ${data.item.usuario_id}` : ''">
              {{ data.item.usuario_nome || (data.item.usuario_id ? `Usuário ${data.item.usuario_id}` : '—') }}
            </span>
          </template>
          <template #cell(representante_nome)="data">{{ data.item.representante_nome || '-' }}</template>
          <template #cell(saldo_antes)="data">{{ formatarSaldo(data.item.saldo_antes) }}</template>
          <template #cell(saldo_depois)="data">{{ formatarSaldo(data.item.saldo_depois) }}</template>
          <template #cell(valor)="data">
            <span :class="Number(data.item.valor) < 0 ? 'text-danger' : 'text-success'">
              {{ formatarMoeda(data.item.valor) }}
            </span>
          </template>
        </b-table>
      </b-card>
    </template>

    <b-card v-else>
      <div class="text-center text-muted py-4">Sem dados para exibir.</div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BSpinner, BTable, BAlert, BButton, BProgress, BProgressBar, BBadge,
  BFormSelect, BFormInput,
} from 'bootstrap-vue';
import { mapActions, mapState } from 'vuex';
import BudgetClient from '@/api/Budget/budget.client';
import Notifier from '@/libs/Notifier';

const budgetClient = new BudgetClient();
const notify = new Notifier();

export default {
  name: 'DashboardBudget',
  components: {
    BCard, BRow, BCol, BSpinner, BTable, BAlert, BButton, BProgress, BProgressBar, BBadge,
    BFormSelect, BFormInput,
  },
  data() {
    return {
      carregando: true,
      // Aprovador alternativo (delegação)
      delegacaoAtiva: null,
      candidatos: [],
      delegacaoSalvando: false,
      delegacaoForm: { delegadoid: null, datainicio: '', datatermino: '', motivo: '' },
      supFields: [
        { key: 'supervisor_nome', label: 'Supervisor' },
        { key: 'categoria', label: 'Categoria', class: 'text-center' },
        { key: 'budgets', label: 'Budgets', class: 'text-center' },
        { key: 'valor', label: 'Valor', class: 'text-right' },
        { key: 'valorgasto', label: 'Gasto', class: 'text-right' },
        { key: 'saldo', label: 'Saldo', class: 'text-right' },
      ],
      movFields: [
        { key: 'datahora', label: 'Data/Hora' },
        { key: 'usuario_nome', label: 'Quem fez' },
        { key: 'categoria', label: 'Categoria', class: 'text-center' },
        { key: 'supervisor_nome', label: 'Supervisor' },
        { key: 'representante_nome', label: 'Representante' },
        { key: 'descricao', label: 'Descrição' },
        { key: 'saldo_antes', label: 'Saldo antes', class: 'text-right' },
        { key: 'saldo_depois', label: 'Saldo depois', class: 'text-right' },
        { key: 'valor', label: 'Valor', class: 'text-right' },
      ],
      rascFields: [
        { key: 'pedidoid', label: 'Rascunho' },
        { key: 'cliente_nome', label: 'Cliente' },
        { key: 'categoria', label: 'Categoria', class: 'text-center' },
        { key: 'representante_nome', label: 'Representante' },
        { key: 'data', label: 'Criado em' },
        { key: 'valor', label: 'Valor previsto', class: 'text-right' },
      ],
      pedFields: [
        { key: 'pedidoid', label: 'Pedido' },
        { key: 'cliente_nome', label: 'Cliente' },
        { key: 'oc_cliente', label: 'OC Cliente' },
        { key: 'representante_nome', label: 'Representante' },
        { key: 'data', label: 'Data/Hora' },
        { key: 'pedido_valor_total', label: 'Valor do pedido', class: 'text-right' },
        { key: 'valor', label: 'Valor dado em baixa', class: 'text-right' },
        { key: 'acoes', label: 'Ações', class: 'text-center' },
      ],
    };
  },
  computed: {
    ...mapState('Budget', ['dashboard', 'isLoading', 'meuSaldo', 'meusPedidos']),
    // A visão é decidida pelo PAPEL do perfil ativo (back: meuSaldo.papel).
    // 'representante' cobre tanto quem tem budget vigente (aplicavel:true) quanto
    // quem não tem (aplicavel:false, semBudget) — ambos veem a visão pessoal e
    // NUNCA o dashboard agregado (que retorna 403 para representante).
    ehRepresentante() {
      return !!(this.meuSaldo && this.meuSaldo.papel === 'representante');
    },
    // Delegação recebida: estou atuando como aprovador alternativo de alguém.
    delegadoDe() {
      return (this.meuSaldo && this.meuSaldo.delegadoDe) || null;
    },
    // Sou o PRÓPRIO supervisor (não um delegado) — posso gerenciar minha delegação.
    ehSupervisorProprio() {
      return !!(this.meuSaldo && this.meuSaldo.papel === 'supervisor' && !this.meuSaldo.delegadoDe);
    },
    candidatosOptions() {
      const opts = this.candidatos.map(u => ({ value: String(u.usuarioid), text: u.usuario_nome || u.usuarioid }));
      return [{ value: null, text: 'Selecione um usuário…' }, ...opts];
    },
    pedidos() {
      return (this.meusPedidos && this.meusPedidos.pedidos) || [];
    },
    rascunhos() {
      return (this.meuSaldo && this.meuSaldo.rascunhos && this.meuSaldo.rascunhos.lista) || [];
    },
    totalRascunhos() {
      if (this.meuSaldo && this.meuSaldo.rascunhos && this.meuSaldo.rascunhos.total != null) {
        return this.meuSaldo.rascunhos.total;
      }
      return this.rascunhos.reduce((acc, r) => acc + (Number(r.valor) || 0), 0);
    },
    totalBaixa() {
      if (this.meusPedidos && this.meusPedidos.total != null) return this.meusPedidos.total;
      return this.pedidos.reduce((acc, p) => acc + (Number(p.valor) || 0), 0);
    },
  },
  async mounted() {
    this.carregando = true;
    try {
      // O papel decide a visão: representante vê a consulta pessoal; supervisor
      // e admin veem o dashboard agregado (este último exige Budget access).
      const saldo = await this.carregarMeuSaldo();
      if (saldo && saldo.papel === 'representante') {
        // Representante (com ou sem budget vigente): só pedidos pessoais.
        // NUNCA chama carregarDashboard → evita o 403 que zerava a tela.
        // O histórico ainda exige responsabilidade resolvida em todas as categorias.
        // Evita um 409 secundário e não apresenta dados anteriores como atuais.
        if (!saldo.saldoParcial) await this.carregarMeusPedidos();
      } else if (saldo) {
        // supervisor / admin — dashboard agregado (exige Budget access).
        // Só chamado quando meuSaldo respondeu: evita que um representante caia
        // no /dashboard (403) num erro de rede e veja um popup de erro.
        await this.carregarDashboard();
        // Gestão do aprovador alternativo (só o próprio supervisor).
        if (saldo.papel === 'supervisor' && !saldo.delegadoDe) await this.carregarDelegacao();
      }
    } finally {
      this.carregando = false;
    }
  },
  methods: {
    ...mapActions('Budget', ['carregarDashboard', 'carregarMeuSaldo', 'carregarMeusPedidos']),
    // Resumo por categoria vindo do back (dashboard.por_categoria) com fallback
    // vazio para retrocompatibilidade enquanto o back não é atualizado.
    // Saldo pessoal por categoria (meuSaldo.categorias) com fallback vazio.
    minhaCategoria(cat) {
      const cs = this.meuSaldo && this.meuSaldo.categorias;
      return (cs && cs[cat]) || {
        temBudget: false, temAlocacao: false, valor: 0, valorgasto: 0, disponivel: 0, rascunho: 0, disponivelAposRascunhos: 0,
      };
    },
    categoriaResumo(cat) {
      const pc = this.dashboard && this.dashboard.por_categoria;
      return (pc && pc[cat]) || { budgets: 0, vigentes: 0, valor_total: 0, gasto_total: 0, saldo_total: 0, rascunho_total: 0 };
    },
    irParaBudget(item) {
      if (item && item.budgetid) {
        this.$router.push({ name: 'budget-detalhes', params: { id: item.budgetid } });
      }
    },
    verPedido(item) {
      if (!item || !item.clienteid) return;
      this.$router.push({
        name: 'pedidos-vendas-detalhes',
        params: { pedidoid: item.pedidoid, clienteid: item.clienteid },
      });
    },
    formatarMoeda(v) {
      return (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    },
    // saldo_antes/saldo_depois pode vir null (lado do pool do supervisor numa
    // transferência, ou logs antigos sem a coluna) → exibe travessão.
    formatarSaldo(v) {
      if (v === null || v === undefined || v === '') return '—';
      return this.formatarMoeda(v);
    },
    async carregarDelegacao() {
      try {
        const { data: res } = await budgetClient.delegacaoStatus();
        this.delegacaoAtiva = (res && res.data && res.data.minha) || null;
        if (!this.delegacaoAtiva) {
          const { data: cand } = await budgetClient.delegacaoCandidatos();
          this.candidatos = (cand && cand.data) || [];
        }
      } catch (e) {
        // Falha silenciosa: o card simplesmente não lista candidatos.
        this.delegacaoAtiva = null;
      }
    },
    async criarDelegacao() {
      if (!this.delegacaoForm.delegadoid) return;
      this.delegacaoSalvando = true;
      try {
        const { data: res } = await budgetClient.criarDelegacao({
          delegadoid: this.delegacaoForm.delegadoid,
          datainicio: this.delegacaoForm.datainicio || undefined,
          datatermino: this.delegacaoForm.datatermino || null,
          motivo: this.delegacaoForm.motivo || null,
        });
        this.delegacaoAtiva = (res && res.data) || null;
        this.delegacaoForm = { delegadoid: null, datainicio: '', datatermino: '', motivo: '' };
        notify.success('Aprovador alternativo designado.');
      } catch (e) {
        // erro já exibido pelo client
      } finally {
        this.delegacaoSalvando = false;
      }
    },
    async encerrarDelegacao() {
      if (!this.delegacaoAtiva) return;
      this.delegacaoSalvando = true;
      try {
        await budgetClient.encerrarDelegacao(this.delegacaoAtiva.delegacaoid);
        this.delegacaoAtiva = null;
        notify.success('Delegação encerrada.');
        await this.carregarDelegacao();
      } catch (e) {
        // erro já exibido pelo client
      } finally {
        this.delegacaoSalvando = false;
      }
    },
    // Texto do aviso quando não há budget VIGENTE na categoria (vencido ou
    // ainda não iniciado) — mesmo critério do widget de saldo da navbar.
    avisoBudgetNaoVigente(cat) {
      const info = this.minhaCategoria(cat).budgetNaoVigente;
      if (!info) return null;
      if (info.situacao === 'vencido') return `Budget vencido em ${this.formatarDataCurta(info.datatermino)}`;
      if (info.situacao === 'futuro') return `Budget inicia em ${this.formatarDataCurta(info.datainicio)}`;
      return 'Budget fora de vigência';
    },
    formatarDataCurta(d) {
      if (!d) return '-';
      const str = String(d).slice(0, 10);
      const [y, m, dd] = str.split('-');
      return (y && m && dd) ? `${dd}/${m}/${y}` : str;
    },
    formatarDataHora(d) {
      if (!d) return '-';
      const dt = new Date(d);
      if (Number.isNaN(dt.getTime())) return d;
      return dt.toLocaleString('pt-BR');
    },
  },
};
</script>

<style scoped>
.page-breadcrumb {
  font-size: 0.85rem;
  color: #6e6b7b;
}
</style>
